// services/accelerators/founders/api_TrackedPrograms.ts
//
// Service for the user-owned "tracked accelerator programs" feature
// (MyAcceleratorTracker). Co-located with api_MyFounders.ts because this is
// user-owned founder data.
//
// Endpoints (all on the existing catch-all API, no new gateway resource):
//   GET    /api/user/tracked-programs        → list
//   POST   /api/user/tracked-programs        → add    { category, id }
//   PUT    /api/user/tracked-programs        → update { category, id, annotations }
//   DELETE /api/user/tracked-programs        → remove { category, id }
//
// Base helpers (API_BASE_URL / getAuthHeaders / handleResponse / the 401
// token-refresh interceptor via authenticatedFetch) are REUSED from the
// canonical ../../api — never redefined here.

import {
  API_BASE_URL,
  getAuthHeaders,
  handleResponse,
  authenticatedFetch,
} from '../../api';
import type { APIResponse } from './MyFounderTypes';
import type {
  TrackedProgram,
  TrackedProgramAnnotations,
} from './TrackedProgramTypes';
import { getMockTrackedPrograms } from './api_MockTrackedPrograms';

const TRACKED_ENDPOINT = `${API_BASE_URL}/user/tracked-programs`;

/**
 * Safe fetch wrapper (mirrors api_MyFounders): degrades network errors to
 * `null` so the caller can fall back to a valid empty result. Uses
 * authenticatedFetch so a 401 transparently refreshes the access token once.
 */
const safeFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response | null> => {
  try {
    return await authenticatedFetch(url, options);
  } catch (error: any) {
    console.error('Network error - backend not available:', error);
    if (
      error?.name === 'TypeError' ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('NetworkError')
    ) {
      return null;
    }
    throw error;
  }
};

/**
 * Normalize the backend payload to a TrackedProgram[]. The backend may return
 * the `tracked_programs` map (keyed by "<category>#<id>"), a values array, or —
 * for a brand-new user whose row has no such field — nothing at all. ANY of
 * those normalizes to `[]` (never null/throw).
 */
const normalizeTrackedPrograms = (raw: any): TrackedProgram[] => {
  const tp =
    raw?.tracked_programs ?? raw?.trackedPrograms ?? raw?.data ?? raw;
  if (!tp || typeof tp !== 'object') return [];

  const values: any[] = Array.isArray(tp) ? tp : Object.values(tp);

  return values
    .filter((v) => v && typeof v === 'object')
    .map((v): TrackedProgram => {
      const rawId = v.id;
      const id = typeof rawId === 'number' ? rawId : parseInt(rawId, 10);
      const ann = v.annotations ?? {};
      return {
        category: v.category,
        id,
        addedAt: v.addedAt ?? v.added_at ?? '',
        annotations: {
          status: ann.status ?? 'saved',
          owner: ann.owner,
          nextStep: ann.nextStep ?? ann.next_step,
          notes: ann.notes,
          updatedAt: ann.updatedAt ?? ann.updated_at,
        },
      };
    })
    .filter((t) => !!t.category && Number.isFinite(t.id));
};

export class TrackedProgramsService {
  /**
   * List the current user's tracked programs. An absent/empty
   * `tracked_programs` field normalizes to `[]` — treated as "no tracked
   * items," not an error. Backend unavailable → mock empty set.
   */
  async getTrackedPrograms(): Promise<APIResponse<TrackedProgram[]>> {
    try {
      const response = await safeFetch(TRACKED_ENDPOINT, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      // Backend not available → valid empty result (never throw).
      if (response === null) {
        return getMockTrackedPrograms();
      }

      // No row / no field yet → empty set.
      if (response.status === 404) {
        return { success: true, data: [] };
      }

      const data = await handleResponse(response);
      return { success: true, data: normalizeTrackedPrograms(data) };
    } catch (error: any) {
      console.error('Error fetching tracked programs:', error);
      // Network errors degrade to an empty result, matching the founder service.
      if (
        error?.name === 'TypeError' ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('NetworkError')
      ) {
        return getMockTrackedPrograms();
      }
      throw error;
    }
  }

  /**
   * Track (add) a program. Idempotent add-only on the backend. POST body is the
   * composite identity only — annotations seed to `{status:'saved'}` server-side.
   */
  async trackProgram(
    category: string,
    id: number
  ): Promise<APIResponse<TrackedProgram | null>> {
    try {
      const response = await safeFetch(TRACKED_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category, id }),
      });

      if (response === null) {
        throw new Error(
          'Backend not available. Please try again when the server is running.'
        );
      }

      const data = await handleResponse(response);
      return {
        success: true,
        data: data?.program ?? null,
      };
    } catch (error) {
      console.error('Error tracking program:', error);
      throw error;
    }
  }

  /**
   * Update a tracked program's workflow annotations (status/owner/nextStep/
   * notes). PUT body shape copied field-for-field from
   * founderService.updateSubmissionProfile. Identity fields are never touched;
   * the backend stamps `updatedAt`.
   */
  async updateTrackedProgram(
    category: string,
    id: number,
    annotations: Partial<TrackedProgramAnnotations>
  ): Promise<APIResponse<TrackedProgram | null>> {
    try {
      const response = await safeFetch(TRACKED_ENDPOINT, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category, id, annotations }),
      });

      if (response === null) {
        throw new Error(
          'Backend not available. Please try again when the server is running.'
        );
      }

      const data = await handleResponse(response);
      return {
        success: true,
        data: data?.program ?? null,
      };
    } catch (error) {
      console.error('Error updating tracked program:', error);
      throw error;
    }
  }

  /**
   * Untrack (remove) a program. DELETE body is the composite identity.
   */
  async untrackProgram(
    category: string,
    id: number
  ): Promise<APIResponse<void>> {
    try {
      const response = await safeFetch(TRACKED_ENDPOINT, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category, id }),
      });

      if (response === null) {
        throw new Error(
          'Backend not available. Please try again when the server is running.'
        );
      }

      // Some backends 204 a delete; only parse when there's a body.
      if (response.status !== 204) {
        await handleResponse(response);
      }
      return { success: true };
    } catch (error) {
      console.error('Error untracking program:', error);
      throw error;
    }
  }
}

export const trackedProgramsService = new TrackedProgramsService();
export default trackedProgramsService;
